import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { RootState } from '../../../../store/root-reducer';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
} from '../../types';
import {
  setActiveLeadType,
  setActiveSubTab,
  clearPeopleSelection,
  clearCompanySelection,
  setBulkPeopleSearch,
  setBulkPeopleModeActive,
} from '../../v2-lead-slice';

interface UseTabNavigationProps {
  setLeadsData: (data: any) => void;
  setCompaniesData: (data: any) => void;
  handleFetchCounts: (payload?: any) => void;
  handleCompanyFetchCounts: (payload?: any) => void;
  searchLeads: (payload?: any) => void;
  searchCompanies: (payload?: any) => void;
  handleRouteChange?: () => void;
  setSavedFilters?: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  handleDefaultValues?: (reset?: boolean) => SavedTabFilters;
  refreshPeopleLists?: () => void;
  refreshCompanyLists?: () => void;
  skipNextCountCallRef?: React.MutableRefObject<boolean>;
  skipUrlPushForBulkSearchRefProp?: React.MutableRefObject<boolean>;
  tabSpecificBulkModeRef?: React.MutableRefObject<{
    people: boolean;
    company: boolean;
  }>;
}

export const useTabNavigation = (props: UseTabNavigationProps) => {
  const {
    setLeadsData,
    setCompaniesData,
    handleFetchCounts,
    handleCompanyFetchCounts,
    searchLeads,
    searchCompanies,
    handleRouteChange,
    setSavedFilters,
    handleDefaultValues,
    refreshPeopleLists,
    refreshCompanyLists,
    skipNextCountCallRef,
    skipUrlPushForBulkSearchRefProp,
    tabSpecificBulkModeRef,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { activeLeadType, activeSubTab, bulkPeopleSearch } = useSelector(
    (state: RootState) => state.leadFinderV2,
  );

  // Own refs
  const preservedFiltersRef = useRef<{
    saved: string;
    totalNew: string;
  }>({ saved: '', totalNew: '' });

  const tabSpecificFiltersRef = useRef<{
    people: string;
    company: string;
    csvEnrichment: string;
  }>({ people: '', company: '', csvEnrichment: '' });

  const tabSpecificSavedFiltersRef = useRef<{
    people: string;
    company: string;
  }>({ people: '', company: '' });

  const skipUrlPushForBulkSearchOwnRef = useRef(false);
  // Use provided ref if available (allows sharing with useLeadFinder/useCompanyFinder)
  const skipUrlPushForBulkSearchRef =
    skipUrlPushForBulkSearchRefProp ?? skipUrlPushForBulkSearchOwnRef;

  /**
   * Returns all URL params except pagination (page, start, take).
   * Used when storing/restoring filters per tab on tab switch.
   */
  const getAllFiltersExceptPagination = useCallback(
    (params: URLSearchParams): URLSearchParams => {
      const result = new URLSearchParams();
      const paginationKeys = new Set(['page', 'start', 'take', 'pageSize']);
      params.forEach((value, key) => {
        if (!paginationKeys.has(key)) {
          result.append(key, value);
        }
      });
      return result;
    },
    [],
  );

  /** Clear all: clear table; call count API only when in bulk search (URL already '', so finder effect won't run) */
  const handleClearAllPeople = useCallback(() => {
    setLeadsData({ profiles: [], pagination: null });
    if (
      skipUrlPushForBulkSearchRef.current &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED
    ) {
      handleFetchCounts({});
      tabSpecificFiltersRef.current.people = '';
      dispatch(setBulkPeopleModeActive(false));
    }
  }, [setLeadsData, activeSubTab, handleFetchCounts, dispatch]);

  /** Clear all: clear table; call count API only when in bulk search (URL already '', so finder effect won't run) */
  const handleClearAllCompany = useCallback(() => {
    setCompaniesData({ profiles: [], pagination: null });
    tabSpecificFiltersRef.current.company = '';
    if (tabSpecificBulkModeRef) {
      tabSpecificBulkModeRef.current.company = false;
    }
    if (
      skipUrlPushForBulkSearchRef.current &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED
    ) {
      handleCompanyFetchCounts({});
    }
  }, [setCompaniesData, activeSubTab, handleCompanyFetchCounts]);

  // Handle lead type tab change (people/company)
  const handleLeadTypeChange = useCallback(
    (newLeadType: LeadFinderPrimaryTab) => {
      if (newLeadType === activeLeadType) {
        if (
          newLeadType === LeadFinderPrimaryTab.CSV_ENRICHMENT &&
          (location.pathname.includes('/csv-enrichment/status') ||
            location.pathname.includes('/csv-enrichment/new'))
        ) {
          history.push('/v2/leads/csv-enrichment');
        }
        return;
      }

      // Navigate to CSV Enrichment route directly, restoring any previously stored filters
      if (newLeadType === LeadFinderPrimaryTab.CSV_ENRICHMENT) {
        const leavingPath = location.pathname || '';
        const isLeavingPeoplePath =
          !leavingPath.includes('/v2/leads/company/') &&
          !leavingPath.startsWith('/v2/leads/csv-enrichment');
        if (isLeavingPeoplePath) {
          if (
            skipUrlPushForBulkSearchRef.current ||
            bulkPeopleSearch.isBulkModeActive
          ) {
            tabSpecificFiltersRef.current.people =
              tabSpecificFiltersRef.current.people ||
              bulkPeopleSearch.filters ||
              '';
          } else {
            tabSpecificFiltersRef.current.people =
              activeSubTab === LeadFinderSecondaryTab.SAVED
                ? preservedFiltersRef.current.totalNew
                : getAllFiltersExceptPagination(
                    new URLSearchParams(location.search),
                  ).toString();
            if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
              tabSpecificSavedFiltersRef.current.people = location.search || '';
            }
          }
        } else if (leavingPath.includes('/v2/leads/company/')) {
          if (tabSpecificBulkModeRef?.current?.company) {
            tabSpecificFiltersRef.current.company =
              tabSpecificFiltersRef.current.company || '';
          } else {
            tabSpecificFiltersRef.current.company =
              activeSubTab === LeadFinderSecondaryTab.SAVED
                ? preservedFiltersRef.current.totalNew
                : getAllFiltersExceptPagination(
                    new URLSearchParams(location.search),
                  ).toString();
            if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
              tabSpecificSavedFiltersRef.current.company =
                location.search || '';
            }
          }
        }
        dispatch(setActiveLeadType(newLeadType));
        const storedSearch = tabSpecificFiltersRef.current.csvEnrichment;
        history.push(`/v2/leads/csv-enrichment${storedSearch || ''}`);
        return;
      }

      const currentParams = new URLSearchParams(location.search);

      // Step 1: Store current tab's filters (all except pagination) before switching away
      const currentTabFilters = getAllFiltersExceptPagination(currentParams);
      const currentPath = location.pathname || '';
      const isCsvEnrichment = currentPath.startsWith(
        '/v2/leads/csv-enrichment',
      );
      // eslint-disable-next-line no-nested-ternary
      const currentTabKey = currentPath.includes('/v2/leads/company/')
        ? 'company'
        : isCsvEnrichment
        ? 'csvEnrichment'
        : 'people';
      const isLeavingPeopleInBulkMode =
        currentTabKey === 'people' &&
        (skipUrlPushForBulkSearchRef.current ||
          bulkPeopleSearch.isBulkModeActive);
      const isLeavingCompanyInBulkMode =
        currentTabKey === 'company' &&
        !!tabSpecificBulkModeRef?.current?.company;

      if (isLeavingPeopleInBulkMode) {
        tabSpecificFiltersRef.current.people =
          tabSpecificFiltersRef.current.people ||
          bulkPeopleSearch.filters ||
          '';
      } else if (isLeavingCompanyInBulkMode) {
        tabSpecificFiltersRef.current.company =
          tabSpecificFiltersRef.current.company || '';
      } else if (!isCsvEnrichment) {
        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          if (
            currentTabKey === LeadFinderPrimaryTab.PEOPLE ||
            currentTabKey === LeadFinderPrimaryTab.COMPANY
          ) {
            tabSpecificSavedFiltersRef.current[currentTabKey] =
              location.search || '';
          }
          [
            'people_list',
            'excludePeople_list',
            'company_list',
            'excludeCompany_list',
          ].forEach((key) => currentTabFilters.delete(key));
        }
        const filtersToStore =
          (currentTabKey === LeadFinderPrimaryTab.PEOPLE ||
            currentTabKey === LeadFinderPrimaryTab.COMPANY) &&
          activeSubTab === LeadFinderSecondaryTab.SAVED
            ? preservedFiltersRef.current.totalNew
            : currentTabFilters.toString();
        tabSpecificFiltersRef.current[currentTabKey] = filtersToStore;
      } else {
        tabSpecificFiltersRef.current.csvEnrichment = location.search;
      }

      // Step 2: Get stored filters for the target tab
      const targetTabKey =
        newLeadType === LeadFinderPrimaryTab.PEOPLE ? 'people' : 'company';
      if (
        targetTabKey === 'company' &&
        !tabSpecificFiltersRef.current.company &&
        (location as any)?.state?.preservedCompanyFilters
      ) {
        tabSpecificFiltersRef.current.company = String(
          (location as any).state.preservedCompanyFilters,
        );
      }
      let storedParams = new URLSearchParams();
      const storedPeopleFilters = (
        tabSpecificFiltersRef.current.people || ''
      ).trim();
      const isCurrentlyOnPeopleTab = currentTabKey === 'people';
      const shouldRestorePeopleBulkFromRef =
        newLeadType === LeadFinderPrimaryTab.PEOPLE &&
        storedPeopleFilters.length > 0 &&
        (bulkPeopleSearch.isBulkModeActive ||
          (isCurrentlyOnPeopleTab && skipUrlPushForBulkSearchRef.current) ||
          !!tabSpecificBulkModeRef?.current?.people);

      if (shouldRestorePeopleBulkFromRef) {
        dispatch(
          setBulkPeopleSearch({
            filters: tabSpecificFiltersRef.current.people,
          }),
        );
        dispatch(setBulkPeopleModeActive(true));
      } else if (
        targetTabKey === 'company' &&
        tabSpecificBulkModeRef?.current?.company &&
        tabSpecificFiltersRef.current.company
      ) {
        storedParams = new URLSearchParams();
      } else if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
        if (tabSpecificSavedFiltersRef.current[targetTabKey]) {
          storedParams = new URLSearchParams(
            tabSpecificSavedFiltersRef.current[targetTabKey],
          );
        }
      } else if (tabSpecificFiltersRef.current[targetTabKey]) {
        storedParams = new URLSearchParams(
          tabSpecificFiltersRef.current[targetTabKey],
        );
      }

      const shouldKeepUrlCleanForTargetTab =
        (targetTabKey === 'company' &&
          tabSpecificBulkModeRef?.current?.company) ||
        (targetTabKey === 'people' && tabSpecificBulkModeRef?.current?.people);

      // Step 3: Preserve pagination params from current URL if desired
      if (!isCsvEnrichment) {
        const paginationKeys = ['page', 'start', 'take'];
        paginationKeys.forEach((key) => {
          const value = currentParams.get(key);
          if (value) {
            storedParams.set(key, value);
          }
        });
      }

      // Step 4: Clear selection state for the tab we're leaving
      if (newLeadType === LeadFinderPrimaryTab.COMPANY) {
        dispatch(clearPeopleSelection());
      } else {
        dispatch(clearCompanySelection());
      }

      // Step 5: Update state and navigate
      dispatch(setActiveLeadType(newLeadType));
      setLeadsData({
        profiles: [],
        pagination: null,
      });
      if (!isCsvEnrichment && handleRouteChange) {
        handleRouteChange();
      }
      if (setSavedFilters && handleDefaultValues) {
        setSavedFilters(handleDefaultValues(true));
      }
      preservedFiltersRef.current = { saved: '', totalNew: '' };
      if (
        activeSubTab === LeadFinderSecondaryTab.SAVED &&
        tabSpecificFiltersRef.current[targetTabKey]
      ) {
        preservedFiltersRef.current.totalNew =
          tabSpecificFiltersRef.current[targetTabKey];
      }

      // Ensure CSV-enrichment-specific pagination never leaks into people/company URLs
      // storedParams.delete('page');
      // storedParams.delete('pageSize');

      if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
        ['createdDate', 'tags', 'search', 'excludeProspects'].forEach((key) =>
          storedParams.delete(key),
        );
      }

      const newSearch = shouldKeepUrlCleanForTargetTab
        ? ''
        : storedParams.toString();
      if (
        isCsvEnrichment &&
        newLeadType === LeadFinderPrimaryTab.PEOPLE &&
        shouldRestorePeopleBulkFromRef &&
        skipNextCountCallRef
      ) {
        skipNextCountCallRef.current = true;
      }
      history.push(
        `/v2/leads/${newLeadType}/${activeSubTab}${
          newSearch ? `?${newSearch}` : ''
        }`,
      );

      if (newLeadType === LeadFinderPrimaryTab.PEOPLE) {
        if (refreshCompanyLists) refreshCompanyLists();
      } else if (refreshPeopleLists) refreshPeopleLists();
    },
    [
      activeLeadType,
      activeSubTab,
      dispatch,
      setLeadsData,
      handleRouteChange,
      setSavedFilters,
      handleDefaultValues,
      location.search,
      history,
      getAllFiltersExceptPagination,
      refreshCompanyLists,
      refreshPeopleLists,
      bulkPeopleSearch.isBulkModeActive,
      bulkPeopleSearch.filters,
      skipUrlPushForBulkSearchRef,
      skipNextCountCallRef,
      tabSpecificBulkModeRef,
    ],
  );

  // Handle subtab change (total/new/saved)
  const handleSubTabChange = useCallback(
    (mappedSubTab: LeadFinderSecondaryTab) => {
      if (mappedSubTab === activeSubTab) {
        return;
      }

      const isMovingToSaved = mappedSubTab === LeadFinderSecondaryTab.SAVED;
      const isMovingFromSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
      const isPersistable =
        (activeSubTab === LeadFinderSecondaryTab.TOTAL &&
          mappedSubTab === LeadFinderSecondaryTab.NEW) ||
        (activeSubTab === LeadFinderSecondaryTab.NEW &&
          mappedSubTab === LeadFinderSecondaryTab.TOTAL);

      const isInBulkMode =
        skipUrlPushForBulkSearchRef.current ||
        (activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
          bulkPeopleSearch.isBulkModeActive);

      dispatch(setActiveSubTab(mappedSubTab));
      setLeadsData({
        profiles: [],
        pagination: null,
      });
      dispatch(clearPeopleSelection());
      dispatch(clearCompanySelection());
      if (refreshPeopleLists) refreshPeopleLists();
      if (refreshCompanyLists) refreshCompanyLists();

      let newUrl = '';

      if (isMovingToSaved || isMovingFromSaved) {
        if (isMovingToSaved) {
          // Store current total/new filters before switching to saved tab (unless bulk search mode)
          if (!isInBulkMode) {
            preservedFiltersRef.current.totalNew = location.search || '';
          }
        } else if (isMovingFromSaved) {
          // Store current saved tab filters before switching away (unless bulk search mode)
          if (!isInBulkMode) {
            preservedFiltersRef.current.saved = location.search || '';
          }
        }
        if (handleRouteChange) handleRouteChange();
        if (setSavedFilters && handleDefaultValues) {
          setSavedFilters(handleDefaultValues(true));
        }

        if (isInBulkMode) {
          if (
            isMovingFromSaved &&
            activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
            tabSpecificFiltersRef.current.people
          ) {
            dispatch(
              setBulkPeopleSearch({
                filters: tabSpecificFiltersRef.current.people,
              }),
            );
            if (skipNextCountCallRef) {
              skipNextCountCallRef.current = true;
            }
          }
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
        } else if (isMovingFromSaved) {
          // When moving FROM saved to total/new, restore preserved total/new filters if they exist
          const preservedSearch = preservedFiltersRef.current.totalNew || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${preservedSearch}`;
        } else {
          // When moving to saved tab, restore preserved saved tab filters if they exist
          const preservedSearch = preservedFiltersRef.current.saved || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${preservedSearch}`;
        }
      } else if (isPersistable) {
        if (isInBulkMode) {
          if (
            activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
            tabSpecificFiltersRef.current.people
          ) {
            dispatch(
              setBulkPeopleSearch({
                filters: tabSpecificFiltersRef.current.people,
              }),
            );
          }
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
        } else {
          const currentSearch = location.search || '';
          newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}${currentSearch}`;
        }
      } else {
        // Default: clear filters
        if (handleRouteChange) handleRouteChange();
        if (setSavedFilters && handleDefaultValues) {
          setSavedFilters(handleDefaultValues(true));
        }
        newUrl = `/v2/leads/${activeLeadType}/${mappedSubTab}`;
      }

      // Update URL
      history.push(newUrl);
    },
    [
      activeSubTab,
      activeLeadType,
      bulkPeopleSearch.isBulkModeActive,
      dispatch,
      setLeadsData,
      handleRouteChange,
      setSavedFilters,
      handleDefaultValues,
      location.search,
      history,
      refreshPeopleLists,
      refreshCompanyLists,
      skipNextCountCallRef,
    ],
  );

  return {
    preservedFiltersRef,
    tabSpecificFiltersRef,
    skipUrlPushForBulkSearchRef,
    handleLeadTypeChange,
    handleSubTabChange,
    handleClearAllPeople,
    handleClearAllCompany,
    getAllFiltersExceptPagination,
  };
};
